import React, { useEffect, useState } from 'react';
import { X } from 'lucide-react';
import { supabase } from '@/lib/supabase';
import { useAuth } from '@/contexts/AuthContext';
import { toast } from 'sonner';
import { CHANNELS, CAMPAIGN_TYPES, CAMPAIGN_STATUS } from '@/lib/constants';

export default function CampaignSlideOver({ open, onClose, campaign, onSaved, projects = [] }) {
    const { profile } = useAuth();
    const isEdit = !!campaign?.id;
    const [form, setForm] = useState({});
    const [saving, setSaving] = useState(false);

    useEffect(() => {
        if (!open) return;
        setForm(campaign ? { ...campaign } : { name: '', channel: 'Instagram', status: 'Planning' });
    }, [open, campaign?.id]);

    if (!open) return null;
    const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

    const submit = async (e) => {
        e.preventDefault();
        setSaving(true);
        try {
            const payload = { ...form };
            for (const k of Object.keys(payload)) if (payload[k] === '') payload[k] = null;
            payload.budget_aed = payload.budget_aed ? Number(payload.budget_aed) : null;
            payload.actual_spend_aed = payload.actual_spend_aed ? Number(payload.actual_spend_aed) : 0;
            payload.leads_generated = payload.leads_generated ? Number(payload.leads_generated) : 0;
            payload.deals_closed = payload.deals_closed ? Number(payload.deals_closed) : 0;

            if (isEdit) {
                const { error } = await supabase.from('campaigns').update(payload).eq('id', campaign.id);
                if (error) throw error;
            } else {
                payload.organization_id = profile.organization_id;
                payload.created_by = profile.id;
                const { error } = await supabase.from('campaigns').insert(payload);
                if (error) throw error;
            }
            toast.success(isEdit ? 'Campaign updated' : 'Campaign created');
            onSaved?.();
            onClose();
        } catch (err) { toast.error(err.message); } finally { setSaving(false); }
    };

    return (
        <div className="fixed inset-0 z-[60] flex justify-end" onClick={onClose}>
            <div className="absolute inset-0 bg-black/40" />
            <form onSubmit={submit} onClick={(e) => e.stopPropagation()} className="relative bg-white w-full max-w-lg h-full overflow-y-auto shadow-2xl" data-testid="campaign-slideover">
                <div className="sticky top-0 bg-white border-b border-gray-100 p-5 flex items-center justify-between z-10">
                    <h2 className="text-xl font-bold">{isEdit ? 'Edit campaign' : 'New campaign'}</h2>
                    <button type="button" onClick={onClose}><X className="h-5 w-5" /></button>
                </div>
                <div className="p-5 space-y-3">
                    <Field label="Campaign name *"><input required value={form.name || ''} onChange={set('name')} className="input" data-testid="campaign-name" /></Field>
                    <div className="grid grid-cols-2 gap-3">
                        <Field label="Channel *"><select required value={form.channel || 'Instagram'} onChange={set('channel')} className="input" data-testid="campaign-channel">{CHANNELS.map((c) => <option key={c} value={c}>{c}</option>)}</select></Field>
                        <Field label="Type"><select value={form.campaign_type || ''} onChange={set('campaign_type')} className="input"><option value="">—</option>{CAMPAIGN_TYPES.map((t) => <option key={t} value={t}>{t}</option>)}</select></Field>
                        <Field label="Status"><select value={form.status || 'Planning'} onChange={set('status')} className="input">{CAMPAIGN_STATUS.map((s) => <option key={s} value={s}>{s}</option>)}</select></Field>
                        <Field label="Linked project"><select value={form.linked_project_id || ''} onChange={set('linked_project_id')} className="input"><option value="">—</option>{projects.map((p) => <option key={p.id} value={p.id}>{p.project_name}</option>)}</select></Field>
                        <Field label="Start date"><input type="date" value={form.start_date || ''} onChange={set('start_date')} className="input" /></Field>
                        <Field label="End date"><input type="date" value={form.end_date || ''} onChange={set('end_date')} className="input" /></Field>
                        <Field label="Budget (AED)"><input type="number" value={form.budget_aed || ''} onChange={set('budget_aed')} className="input" /></Field>
                        <Field label="Actual spend (AED)"><input type="number" value={form.actual_spend_aed || ''} onChange={set('actual_spend_aed')} className="input" /></Field>
                        <Field label="Leads generated"><input type="number" value={form.leads_generated || ''} onChange={set('leads_generated')} className="input" /></Field>
                        <Field label="Deals closed"><input type="number" value={form.deals_closed || ''} onChange={set('deals_closed')} className="input" /></Field>
                    </div>
                    <Field label="Notes"><textarea rows={3} value={form.notes || ''} onChange={set('notes')} className="input" /></Field>
                </div>
                <div className="sticky bottom-0 bg-white border-t border-gray-100 p-4 flex gap-3">
                    <button type="button" onClick={onClose} className="flex-1 h-11 rounded-md border border-gray-300 font-medium">Cancel</button>
                    <button type="submit" disabled={saving} className="flex-1 h-11 rounded-md bg-aks-navy text-white font-semibold disabled:opacity-60" data-testid="campaign-submit">{saving ? 'Saving…' : 'Save campaign'}</button>
                </div>
                <style>{`.input{margin-top:.25rem;width:100%;height:2.5rem;padding:0 .75rem;border:1px solid #d1d5db;border-radius:.375rem;outline:none;font-size:14px;background:#fff}.input:focus{border-color:#C9A84C;box-shadow:0 0 0 3px rgba(201,168,76,.25)}textarea.input{height:auto;padding:.5rem .75rem}`}</style>
            </form>
        </div>
    );
}

function Field({ label, children }) { return <label className="block"><span className="text-[12px] font-medium text-aks-text">{label}</span>{children}</label>; }
